/* Lumen theme — storefront behaviour. No dependencies. */
(() => {
  'use strict';

  const T = window.theme || {};
  const R = T.routes || {};
  const S = T.strings || {};
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const storage = {
    get(store, key) {
      try {
        return window[store].getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(store, key, value) {
      try {
        window[store].setItem(key, value);
      } catch (e) {
        /* storage unavailable */
      }
    },
  };

  function formatMoney(cents, format = T.moneyFormat || '{{amount}}') {
    const value = Math.round(Number(cents) || 0);
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    let out;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        out = fmt(value, 0);
        break;
      case 'amount_with_comma_separator':
        out = fmt(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = fmt(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = fmt(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        out = fmt(value, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        out = fmt(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        out = fmt(value, 2, ' ', '.');
        break;
      default:
        out = fmt(value, 2);
    }
    return match ? format.replace(match[0], out) : out;
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || S.cartError);
    }
    return data;
  }

  const getSection = (el) => el.closest('[data-product-section]');
  const getProduct = (section) => {
    if (!section) return null;
    if (!section._product) {
      const node = section.querySelector('[data-product-json]');
      section._product = node ? JSON.parse(node.textContent) : null;
    }
    return section._product;
  };

  /* ----------------------------------------------------------------- cart */

  // Store-currency amounts (thresholds) converted to the shopper's currency.
  const currencyRate = () => parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
  const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

  function syncProgress(scope = document) {
    scope.querySelectorAll('[data-progress]').forEach((el) => {
      const threshold = Math.round(Number(el.dataset.threshold) * currencyRate());
      const total = Number(el.dataset.total) || 0;
      if (!threshold) return;
      const remaining = threshold - total;
      const done = remaining <= 0;
      el.classList.toggle('is-complete', done);
      const text = el.querySelector('[data-progress-text]');
      if (text) {
        text.innerHTML = done
          ? escapeHtml(el.dataset.success || '')
          : escapeHtml(el.dataset.text || '').replace('[amount]', `<strong>${formatMoney(remaining)}</strong>`);
      }
      const pct = Math.min(100, (total * 100) / threshold);
      const bar = el.querySelector('.cart-progress__bar');
      if (bar) {
        bar.setAttribute('aria-valuenow', String(Math.round(pct)));
        const fill = bar.querySelector('span');
        if (fill) fill.style.width = `${pct}%`;
      }
    });
  }

  const Cart = {
    get: () => fetch(`${R.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
    add: (items) => postJSON(`${R.cartAdd}.js`, { items }),
    change: (body) => postJSON(`${R.cartChange}.js`, body),
    update: (body) => postJSON(`${R.cartUpdate}.js`, body),
  };

  const isGoalGift = (item) => Boolean(item.properties && item.properties._cart_goal_gift);
  const isBundleGift = (item) => Boolean(item.properties && item.properties._gift_for);

  /**
   * Keeps automatic cart lines honest after every change:
   * bundle gifts need their qualifying quantity, the spend-goal gift needs the
   * threshold, shipping protection is capped at one and never left alone.
   */
  async function syncCart() {
    const cfg = T.cart || {};
    const cart = await Cart.get();
    const changes = new Map();
    const protection = cfg.protectionVariant;

    const regular = cart.items.filter((i) => !isGoalGift(i) && !isBundleGift(i) && i.variant_id !== protection);
    const qtyByProduct = {};
    regular.forEach((i) => {
      qtyByProduct[i.product_id] = (qtyByProduct[i.product_id] || 0) + i.quantity;
    });

    cart.items.forEach((item) => {
      if (!regular.length && (isGoalGift(item) || isBundleGift(item) || item.variant_id === protection)) {
        changes.set(item.key, 0);
        return;
      }
      if (isBundleGift(item)) {
        const need = Math.max(1, parseInt(item.properties._gift_min, 10) || 1);
        const allowed = Math.floor((qtyByProduct[item.properties._gift_for] || 0) / need);
        if (item.quantity > allowed) changes.set(item.key, allowed);
      }
      if (protection && item.variant_id === protection && item.quantity > 1) changes.set(item.key, 1);
    });

    let addGoalGift = false;
    const giftThreshold = Math.round((cfg.giftThreshold || 0) * currencyRate());
    if (cfg.giftVariant && giftThreshold > 0) {
      const goalLine = cart.items.find(isGoalGift);
      const subtotal = cart.items.filter((i) => !isGoalGift(i)).reduce((sum, i) => sum + i.final_line_price, 0);
      const qualifies = regular.length > 0 && subtotal >= giftThreshold;
      if (qualifies && !goalLine) addGoalGift = true;
      if (!qualifies && goalLine) changes.set(goalLine.key, 0);
      if (qualifies && goalLine && goalLine.quantity > 1) changes.set(goalLine.key, 1);
    }

    for (const [id, quantity] of changes) {
      await Cart.change({ id, quantity });
    }
    if (addGoalGift) {
      await Cart.add([{ id: cfg.giftVariant, quantity: 1, properties: { _cart_goal_gift: 'true' } }]).catch(() => {});
    }
  }

  const CartUI = {
    async refresh({ sync = true } = {}) {
      if (sync) await syncCart().catch(() => {});
      const roots = [...document.querySelectorAll('cart-root')];
      const ids = [...new Set(roots.map((r) => r.dataset.section))];
      let count = null;

      if (ids.length) {
        const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
        const data = await res.json();
        roots.forEach((root) => {
          const html = data[root.dataset.section];
          if (!html) return;
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-root');
          if (!fresh) return;
          const scroller = root.querySelector('[data-cart-scroll]');
          const scrollTop = scroller ? scroller.scrollTop : 0;
          [...fresh.attributes].forEach((attr) => root.setAttribute(attr.name, attr.value));
          root.innerHTML = fresh.innerHTML;
          syncProgress(root);
          const newScroller = root.querySelector('[data-cart-scroll]');
          if (newScroller) newScroller.scrollTop = scrollTop;
          count = Number(fresh.dataset.itemCount);
        });
      }
      if (count === null) count = (await Cart.get()).item_count;

      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },
  };

  class CartRoot extends HTMLElement {
    connectedCallback() {
      syncProgress(this);
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.debouncedQty = debounce((key, qty) => this.run(() => Cart.change({ id: key, quantity: qty })), 450);
    }

    async run(task) {
      if (this.classList.contains('is-loading')) return;
      this.classList.add('is-loading');
      this.showError('');
      try {
        await task();
        await CartUI.refresh();
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    showError(message) {
      const el = this.querySelector('[data-cart-errors]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    onClick(e) {
      const step = e.target.closest('[data-qty-change]');
      if (step) {
        const line = step.closest('[data-key]');
        const input = line.querySelector('[data-qty-input]');
        const qty = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyChange));
        input.value = qty;
        this.run(() => Cart.change({ id: line.dataset.key, quantity: qty }));
        return;
      }
      const remove = e.target.closest('[data-remove]');
      if (remove) {
        const line = remove.closest('[data-key]');
        line.classList.add('is-removing');
        this.run(() => Cart.change({ id: line.dataset.key, quantity: 0 }));
        return;
      }
      const upsell = e.target.closest('[data-upsell-add]');
      if (upsell) {
        const select = upsell.closest('.upsell-card').querySelector('[data-upsell-variant]');
        const id = Number(select ? select.value : upsell.dataset.upsellAdd);
        upsell.classList.add('is-loading');
        this.run(() => Cart.add([{ id, quantity: 1 }]));
      }
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-qty-input]')) {
        const qty = Math.max(0, parseInt(t.value, 10) || 0);
        this.debouncedQty(t.closest('[data-key]').dataset.key, qty);
      } else if (t.matches('[data-protection]')) {
        const id = Number(t.value);
        this.run(() => (t.checked ? Cart.add([{ id, quantity: 1 }]) : Cart.change({ id, quantity: 0 })));
      } else if (t.matches('[data-terms]')) {
        const err = this.querySelector('[data-terms-error]');
        if (err && t.checked) err.hidden = true;
      } else if (t.matches('[data-cart-note]')) {
        Cart.update({ note: t.value }).catch(() => {});
      }
    }

    onSubmit(e) {
      const terms = this.querySelector('[data-terms]');
      if (terms && !terms.checked) {
        e.preventDefault();
        const err = this.querySelector('[data-terms-error]');
        if (err) err.hidden = false;
        terms.focus();
        return;
      }
      const btn = this.querySelector('[data-checkout]');
      if (btn) btn.classList.add('is-loading');
      const code = this.dataset.discountCode;
      if (code) {
        e.preventDefault();
        window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
      }
    }
  }
  customElements.define('cart-root', CartRoot);

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      setTimeout(() => this.panel && this.panel.focus(), 50);
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    const drawer = document.querySelector('cart-drawer');
    if (!toggle || !drawer || T.cartType !== 'drawer') return;
    e.preventDefault();
    drawer.open(toggle);
  });

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) CartUI.refresh({ sync: false }).catch(() => {});
  });

  /* -------------------------------------------------------- product form */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    buildItems() {
      const section = getSection(this);
      const bundle = this.hasAttribute('data-main-form') && section ? section.querySelector('bundle-offer') : null;
      if (bundle) return bundle.getPayload();

      const data = new FormData(this.form);
      const item = { id: Number(data.get('id')), quantity: Math.max(1, Number(data.get('quantity')) || 1) };
      const properties = {};
      for (const [key, value] of data.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
      }
      if (Object.keys(properties).length) item.properties = properties;
      if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));
      return { items: [item], code: '' };
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.button || this.button.disabled || this.button.classList.contains('is-loading')) return;
      const section = getSection(this);
      const { items, code } = this.buildItems();

      this.setLoading(true);
      this.showError('');
      try {
        await Cart.add(items);
        if (code) await Cart.update({ attributes: { _bundle_discount: code } });
        const drawer = document.querySelector('cart-drawer');
        if (T.cartType !== 'drawer' || !drawer) {
          window.location.href = R.cart;
          return;
        }
        await CartUI.refresh();
        drawer.open(this.button);
        if (section) section.dispatchEvent(new CustomEvent('product:added'));
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(on) {
      this.button.classList.toggle('is-loading', on);
      this.button.setAttribute('aria-busy', String(on));
      const section = getSection(this);
      if (section) section.dispatchEvent(new CustomEvent('product-form:loading', { detail: on }));
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------- prices */

  function renderPrice(variant, { large = false, badge = true } = {}) {
    if (!variant) return '';
    const onSale = variant.compare_at_price > variant.price;
    const classes = ['price'];
    if (onSale) classes.push('price--on-sale');
    if (!variant.available) classes.push('price--sold-out');
    if (large) classes.push('price--large');
    let html = `<div class="${classes.join(' ')}" data-price><span class="price__current">${formatMoney(variant.price)}</span>`;
    if (onSale) html += `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`;
    if (badge) {
      if (!variant.available) {
        html += `<span class="badge badge--sold-out">${S.soldOut}</span>`;
      } else if (onSale && T.saleBadgeStyle !== 'none') {
        let text = S.onSale;
        if (T.saleBadgeStyle === 'percent') {
          const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          text = S.savePercent.replace('__P__', pct);
        } else if (T.saleBadgeStyle === 'amount') {
          text = S.saveAmount.replace('__A__', formatMoney(variant.compare_at_price - variant.price));
        }
        html += `<span class="badge badge--sale">${text}</span>`;
      }
    }
    return `${html}</div>`;
  }

  /* ------------------------------------------------------ variant picker */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = getSection(this);
      this.product = getProduct(this.section);
      if (!this.product) return;
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability(this.selectedOptions());
    }

    selectedOptions() {
      return this.product.options.map((_, i) => {
        const radio = this.querySelector(`input[data-option-index="${i}"]:checked`);
        const select = this.querySelector(`select[data-option-index="${i}"]`);
        if (radio) return radio.value;
        return select ? select.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.product.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.querySelectorAll('fieldset').forEach((fieldset, i) => {
        const label = fieldset.querySelector('[data-selected-label]');
        if (label) label.textContent = options[i];
      });
      this.updateAvailability(options);
      this.apply(variant);
      this.section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }

    updateAvailability(options) {
      this.product.options.forEach((_, index) => {
        const isAvailable = (value) =>
          this.product.variants.some(
            (v) => v.available && v.options[index] === value && v.options.every((o, j) => j === index || o === options[j])
          );
        this.querySelectorAll(`input[data-option-index="${index}"]`).forEach((input) => {
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !isAvailable(input.value));
        });
        const select = this.querySelector(`select[data-option-index="${index}"]`);
        if (select) {
          [...select.options].forEach((opt) => {
            opt.textContent = opt.value + (isAvailable(opt.value) ? '' : ` – ${S.unavailable}`);
          });
        }
      });
    }

    apply(variant) {
      const s = this.section;
      const buttons = s.querySelectorAll('[data-atc], [data-sticky-button]');
      const labels = s.querySelectorAll('[data-atc-label]');

      if (!variant) {
        buttons.forEach((b) => (b.disabled = true));
        labels.forEach((l) => (l.textContent = S.unavailable));
        return;
      }

      s.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      s.querySelectorAll('[data-product-price]').forEach((el) => {
        el.innerHTML = renderPrice(variant, { large: true, badge: el.dataset.showBadge !== 'false' });
      });
      s.querySelectorAll('[data-sticky-price]').forEach((el) => {
        el.innerHTML = renderPrice(variant);
      });

      buttons.forEach((b) => (b.disabled = !variant.available));
      labels.forEach((l) => {
        l.textContent = variant.available ? l.dataset.defaultLabel || S.addToCart : S.soldOut;
      });

      const inventory = s.querySelector('[data-inventory]');
      if (inventory) {
        const map = JSON.parse((s.querySelector('[data-inventory-json]') || {}).textContent || '{}');
        const qty = map[variant.id];
        const badge = inventory.querySelector('[data-inventory-badge]');
        const text = inventory.querySelector('[data-inventory-text]');
        const threshold = Number(inventory.dataset.threshold);
        let message = inventory.dataset.textInStock || '';
        if (qty !== null && qty !== undefined && qty > 0 && qty <= threshold) {
          message = inventory.dataset.textLow.replace('[count]', qty);
        }
        if (!variant.available) message = '';
        text.textContent = message;
        badge.hidden = !message;
      }

      if (!designMode && s.dataset.url) {
        window.history.replaceState({}, '', `${s.dataset.url}?variant=${variant.id}`);
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* -------------------------------------------------------- bundle offer */

  class BundleOffer extends HTMLElement {
    connectedCallback() {
      this.section = getSection(this);
      this.product = getProduct(this.section);
      if (!this.product) return;
      this.tiers = [...this.querySelectorAll('[data-tier]')];
      this.addons = this.querySelector('[data-addons]');
      const idInput = this.section.querySelector('[data-variant-id]');
      const currentId = idInput ? Number(idInput.value) : null;
      this.variant =
        this.product.variants.find((v) => v.id === currentId) ||
        this.product.variants.find((v) => v.available) ||
        this.product.variants[0];

      if (!this.tiers.some((t) => this.input(t).checked) && this.tiers[0]) this.input(this.tiers[0]).checked = true;

      this.addEventListener('change', (e) => {
        if (e.target.matches('.bundle-tier__input')) this.select();
        else this.updatePrices();
      });
      this.section.addEventListener('variant:change', (e) => {
        if (!e.detail.variant) return;
        this.variant = e.detail.variant;
        this.renderUnits(true);
        this.updatePrices();
      });
      this.select();
    }

    input(tier) {
      return tier.querySelector('.bundle-tier__input');
    }

    get selected() {
      return this.tiers.find((t) => this.input(t).checked) || this.tiers[0];
    }

    get hasVariants() {
      return this.product.variants.length > 1;
    }

    select() {
      const selected = this.selected;
      this.tiers.forEach((t) => t.classList.toggle('is-selected', t === selected));
      if (this.addons && selected) selected.querySelector('[data-addon-slot]').append(this.addons);
      this.renderUnits(false);
      this.updatePrices();
    }

    renderUnits(reset) {
      if (!this.hasVariants) return;
      this.tiers.forEach((tier) => {
        const box = tier.querySelector('[data-units]');
        const isSelected = tier === this.selected;
        box.hidden = !isSelected;
        if (!isSelected) return;
        const qty = Number(tier.dataset.qty);
        const previous = [...box.querySelectorAll('select')].map((s) => s.value);
        if (!reset && previous.length === qty) return;
        box.innerHTML = '';
        for (let i = 0; i < qty; i += 1) {
          const row = document.createElement('div');
          row.className = 'bundle-unit';
          const label = document.createElement('span');
          label.className = 'bundle-unit__index';
          label.textContent = `#${i + 1}`;
          const wrap = document.createElement('div');
          wrap.className = 'select select--small';
          const select = document.createElement('select');
          select.setAttribute('data-unit', '');
          select.setAttribute('aria-label', `${this.product.title} #${i + 1}`);
          this.product.variants.forEach((v) => {
            const opt = document.createElement('option');
            opt.value = v.id;
            opt.textContent = v.title + (v.available ? '' : ` – ${S.soldOut}`);
            opt.disabled = !v.available;
            select.append(opt);
          });
          select.value = String(!reset && previous[i] ? previous[i] : this.variant.id);
          wrap.append(select);
          row.append(label, wrap);
          box.append(row);
        }
      });
    }

    unitVariants(tier) {
      const qty = Number(tier.dataset.qty);
      const selects = [...tier.querySelectorAll('[data-unit]')];
      if (!this.hasVariants || selects.length !== qty) return Array(qty).fill(this.variant);
      return selects.map((s) => this.product.variants.find((v) => String(v.id) === s.value) || this.variant);
    }

    tierPrice(tier) {
      const variants = this.unitVariants(tier);
      const full = variants.reduce((sum, v) => sum + v.price, 0);
      const compare = variants.reduce((sum, v) => sum + Math.max(v.compare_at_price || 0, v.price), 0);
      const discount = parseFloat(tier.dataset.discount) || 0;
      return { price: Math.round((full * (100 - discount)) / 100), compare };
    }

    updatePrices() {
      this.tiers.forEach((tier) => {
        const { price, compare } = this.tierPrice(tier);
        tier.querySelector('[data-tier-price]').innerHTML = formatMoney(price);
        const compareEl = tier.querySelector('[data-tier-compare]');
        compareEl.innerHTML = formatMoney(compare);
        compareEl.hidden = compare <= price;
      });
      const total = this.querySelectorAll('[data-addon]:checked');
      let sum = this.tierPrice(this.selected).price;
      total.forEach((a) => (sum += Number(a.dataset.price) || 0));
      const totalEl = this.section.querySelector('[data-atc-total]');
      if (totalEl) totalEl.innerHTML = this.dataset.showTotal === 'true' ? ` • ${formatMoney(sum)}` : '';

      const unavailable = this.unitVariants(this.selected).some((v) => !v.available);
      this.section.querySelectorAll('[data-atc], [data-sticky-button]').forEach((b) => (b.disabled = unavailable));
    }

    getPayload() {
      const tier = this.selected;
      const counts = new Map();
      this.unitVariants(tier).forEach((v) => counts.set(v.id, (counts.get(v.id) || 0) + 1));
      const items = [...counts].map(([id, quantity]) => ({ id, quantity }));
      if (tier.dataset.giftVariant) {
        items.push({
          id: Number(tier.dataset.giftVariant),
          quantity: 1,
          properties: { _gift_for: String(this.product.id), _gift_min: String(tier.dataset.qty) },
        });
      }
      this.querySelectorAll('[data-addon]:checked').forEach((a) => items.push({ id: Number(a.value), quantity: 1 }));
      return { items, code: tier.dataset.code || '' };
    }
  }
  customElements.define('bundle-offer', BundleOffer);

  /* ------------------------------------------------------ quantity input */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        const min = Number(this.input.min) || 1;
        const next = (parseInt(this.input.value, 10) || min) + (btn.name === 'plus' ? 1 : -1);
        this.input.value = Math.max(min, next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------- media gallery */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-slider]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('.gallery__thumb')];
      this.dots = [...this.querySelectorAll('[data-dots] .dot')];
      this.index = 0;
      this.section = getSection(this);

      this.addEventListener('click', (e) => {
        const go = e.target.closest('[data-go]');
        if (go) return this.goTo(Number(go.dataset.go));
        if (e.target.closest('[data-prev]')) return this.goTo(this.index - 1);
        if (e.target.closest('[data-next]')) return this.goTo(this.index + 1);
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) this.openZoom(zoom.closest('[data-slide]'));
      });
      this.slider.addEventListener('scroll', debounce(() => this.syncFromScroll(), 80), { passive: true });

      if (this.section) {
        this.section.addEventListener('variant:change', (e) => {
          const media = e.detail.variant && e.detail.variant.featured_media;
          if (!media) return;
          const i = this.slides.findIndex((s) => s.dataset.mediaId === String(media.id));
          if (i >= 0) this.goTo(i);
        });
        const product = getProduct(this.section);
        const idInput = this.section.querySelector('[data-variant-id]');
        if (product && idInput && product.variants.length > 1) {
          const variant = product.variants.find((v) => String(v.id) === idInput.value);
          if (variant && variant.featured_media) {
            const i = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.featured_media.id));
            if (i > 0) requestAnimationFrame(() => this.goTo(i, false));
          }
        }
      }
    }

    goTo(i, smooth = true) {
      if (!this.slides.length) return;
      const index = (i + this.slides.length) % this.slides.length;
      this.slider.scrollTo({ left: this.slides[index].offsetLeft, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
      this.setActive(index);
    }

    syncFromScroll() {
      const width = this.slider.clientWidth || 1;
      this.setActive(Math.round(this.slider.scrollLeft / width));
    }

    setActive(index) {
      this.index = index;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      this.thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        if (i === index) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      const thumb = this.thumbs[index];
      if (thumb) {
        const list = thumb.closest('.gallery__thumbs');
        const left = thumb.offsetLeft - list.clientWidth / 2 + thumb.clientWidth / 2;
        list.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
      this.slides.forEach((s, i) => {
        if (i === index) return;
        s.querySelectorAll('video').forEach((v) => v.pause());
      });
    }

    openZoom(slide) {
      const modal = this.section && this.section.querySelector('[data-gallery-modal]');
      if (!modal) return;
      const imageSlides = this.slides.filter((s) => s.querySelector('[data-zoom]'));
      const index = imageSlides.indexOf(slide);
      modal.showModal();
      document.body.classList.add('overflow-hidden');
      const img = modal.querySelectorAll('.gallery-modal__list img')[index];
      if (img) img.scrollIntoView({ block: 'start' });
      if (!modal.bound) {
        modal.bound = true;
        modal.addEventListener('click', (e) => {
          if (e.target.closest('[data-close]') || e.target === modal) modal.close();
        });
        modal.addEventListener('close', () => document.body.classList.remove('overflow-hidden'));
      }
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ----------------------------------------------------- carousel slider */

  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider]');
      if (!this.track) return;
      this.slides = [...this.track.querySelectorAll(':scope > [data-slide]')];
      this.dotsEl = this.querySelector('[data-dots]');
      this.index = 0;
      if (this.slides.length < 2) {
        if (this.dotsEl) this.dotsEl.hidden = true;
        return;
      }
      this.buildDots();
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-prev]')) this.goTo(this.index - 1);
        else if (e.target.closest('[data-next]')) this.goTo(this.index + 1);
        else {
          const dot = e.target.closest('[data-dot]');
          if (dot) this.goTo(Number(dot.dataset.dot));
        }
      });
      this.track.addEventListener('scroll', debounce(() => this.syncFromScroll(), 80), { passive: true });
      window.addEventListener('resize', debounce(() => this.buildDots(), 200));

      if (this.dataset.autoplay === 'true' && !reducedMotion && !designMode) {
        const speed = (Number(this.dataset.speed) || 5) * 1000;
        const start = () => {
          clearInterval(this.timer);
          this.timer = setInterval(() => this.goTo(this.index + 1), speed);
        };
        const stop = () => clearInterval(this.timer);
        this.addEventListener('mouseenter', stop);
        this.addEventListener('mouseleave', start);
        this.addEventListener('focusin', stop);
        this.addEventListener('focusout', start);
        start();
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    get pages() {
      const slideWidth = this.slides[0].offsetWidth || 1;
      const perView = Math.max(1, Math.round(this.track.clientWidth / slideWidth));
      return Math.max(1, this.slides.length - perView + 1);
    }

    buildDots() {
      if (!this.dotsEl) return;
      const pages = this.pages;
      this.dotsEl.hidden = pages < 2;
      this.dotsEl.innerHTML = '';
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `dot${i === this.index ? ' is-active' : ''}`;
        dot.dataset.dot = i;
        dot.setAttribute('aria-label', `${i + 1} / ${pages}`);
        this.dotsEl.append(dot);
      }
    }

    goTo(i) {
      const pages = this.pages;
      let index = i;
      if (index >= pages) index = 0;
      if (index < 0) index = pages - 1;
      this.track.scrollTo({ left: this.slides[index].offsetLeft - this.slides[0].offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      this.setActive(index);
    }

    syncFromScroll() {
      const slideWidth = this.slides[1].offsetLeft - this.slides[0].offsetLeft || 1;
      this.setActive(Math.min(this.pages - 1, Math.round(this.track.scrollLeft / slideWidth)));
    }

    setActive(index) {
      this.index = index;
      if (this.dotsEl) [...this.dotsEl.children].forEach((d, i) => d.classList.toggle('is-active', i === index));
    }
  }
  customElements.define('carousel-slider', CarouselSlider);

  /* ---------------------------------------------------------- sticky ATC */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.section = getSection(this);
      this.mainButton = this.section && this.section.querySelector('[data-atc]');
      if (!this.mainButton) return;
      this.removeAttribute('hidden');
      let frame = null;
      const update = () => {
        frame = null;
        const passed = this.mainButton.getBoundingClientRect().bottom < 0;
        this.classList.toggle('is-visible', passed);
        document.body.classList.toggle('has-sticky-atc', passed);
      };
      window.addEventListener(
        'scroll',
        () => {
          if (!frame) frame = requestAnimationFrame(update);
        },
        { passive: true }
      );
      update();

      const button = this.querySelector('[data-sticky-button]');
      button.addEventListener('click', () => {
        const form = this.section.querySelector('[data-main-form] form');
        if (form) form.requestSubmit();
      });
      this.section.addEventListener('product-form:loading', (e) => button.classList.toggle('is-loading', e.detail));
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ----------------------------------------------------------- countdown */

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.target = this.computeTarget();
      if (!this.target) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    computeTarget() {
      if (this.dataset.mode === 'daily') {
        const [h, m] = (this.dataset.time || '14:00').split(':').map(Number);
        const target = new Date();
        target.setHours(h || 0, m || 0, 0, 0);
        if (target <= new Date()) target.setDate(target.getDate() + 1);
        return target;
      }
      const date = new Date(this.dataset.end);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    tick() {
      const diff = this.target - Date.now();
      if (diff <= 0) {
        if (this.dataset.mode === 'daily') {
          this.target = this.computeTarget();
          return;
        }
        this.expire();
        return;
      }
      const pad = (n) => String(n).padStart(2, '0');
      const set = (sel, value) => {
        const el = this.querySelector(sel);
        if (el) el.textContent = pad(value);
      };
      set('[data-days]', Math.floor(diff / 86400000));
      set('[data-hours]', Math.floor((diff / 3600000) % 24));
      set('[data-minutes]', Math.floor((diff / 60000) % 60));
      set('[data-seconds]', Math.floor((diff / 1000) % 60));
    }

    expire() {
      clearInterval(this.timer);
      if (designMode) return;
      const mode = this.dataset.expired;
      if (mode === 'hide-section') {
        const bar = this.closest('countdown-bar');
        if (bar) bar.hidden = true;
      } else if (mode === 'hide-parent') {
        const parent = this.closest('[data-countdown-parent]') || this.parentElement;
        if (parent) parent.hidden = true;
      }
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  class CountdownBar extends HTMLElement {
    connectedCallback() {
      const key = `lumen-bar-closed-${this.dataset.key}`;
      if (!designMode && this.hasAttribute('data-dismissible') && storage.get('sessionStorage', key)) {
        this.hidden = true;
        return;
      }
      const close = this.querySelector('[data-close]');
      if (close) {
        close.addEventListener('click', () => {
          this.hidden = true;
          storage.set('sessionStorage', key, '1');
        });
      }
    }
  }
  customElements.define('countdown-bar', CountdownBar);

  /* -------------------------------------------------------------- header */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const mode = this.dataset.sticky;
      if (!mode || mode === 'none') return;
      this.wrapper = this.closest('.shopify-section') || this;
      this.wrapper.classList.add('is-sticky');
      if (mode !== 'scroll-up') return;
      let last = window.scrollY;
      window.addEventListener(
        'scroll',
        () => {
          const y = window.scrollY;
          const hide = y > last && y > this.offsetHeight + 150 && !document.body.classList.contains('overflow-hidden');
          this.wrapper.classList.toggle('is-hidden', hide);
          last = y;
        },
        { passive: true }
      );
    }
  }
  customElements.define('sticky-header', StickyHeader);

  class HeaderDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.details.addEventListener('toggle', () => {
        if (this.details.open) {
          const header = this.closest('.header-wrapper') || this;
          const bottom = Math.max(0, header.getBoundingClientRect().bottom);
          document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
        }
        document.body.classList.toggle('overflow-hidden', this.details.open);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.details.open) {
          this.details.open = false;
          this.details.querySelector('summary').focus();
        }
      });
      this.addEventListener('click', (e) => {
        if (e.target.closest('a')) this.details.open = false;
      });
    }
  }
  customElements.define('header-drawer', HeaderDrawer);

  const hoverDetails = document.querySelectorAll('[data-hover-details]');
  hoverDetails.forEach((details) => {
    const canHover = () => window.matchMedia('(hover: hover) and (min-width: 990px)').matches;
    details.addEventListener('mouseenter', () => {
      if (canHover()) details.open = true;
    });
    details.addEventListener('mouseleave', () => {
      if (canHover()) details.open = false;
    });
    details.addEventListener('toggle', () => {
      if (details.open) hoverDetails.forEach((other) => other !== details && (other.open = false));
    });
  });
  document.addEventListener('click', (e) => {
    hoverDetails.forEach((details) => {
      if (details.open && !details.contains(e.target)) details.open = false;
    });
  });

  /* -------------------------------------------------------------- search */

  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-search-modal]');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.dialog) return;
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-open]')) {
          this.dialog.showModal();
          this.input.focus();
        }
      });
      this.dialog.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-close]') || e.target === this.dialog) this.dialog.close();
      });
      this.input.addEventListener('input', debounce(() => this.search(), 250));
    }

    async search() {
      const q = this.input.value.trim();
      if (!q) {
        this.results.innerHTML = '';
        return;
      }
      try {
        const url = `${R.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
        const data = await fetch(url).then((r) => r.json());
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        const esc = (str) => String(str).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
        const items = products
          .map((p) => {
            const image = p.image || (p.featured_image && p.featured_image.url) || '';
            const price = formatMoney(Math.round(parseFloat(p.price) * 100));
            return `<li><a href="${esc(p.url)}" class="search-hit">${
              image ? `<img src="${esc(image)}" alt="" width="64" height="64" loading="lazy">` : ''
            }<span class="search-hit__title">${esc(p.title)}</span><span class="search-hit__price">${price}</span></a></li>`;
          })
          .join('');
        this.results.innerHTML = products.length
          ? `<ul class="search-hits" role="list">${items}</ul><a class="link search-hits__all" href="${R.search}?q=${encodeURIComponent(q)}&options[prefix]=last">${esc(q)} →</a>`
          : `<p class="search-hits__empty">${esc(S.noResults)}</p>`;
      } catch (e) {
        this.results.innerHTML = '';
      }
    }
  }
  customElements.define('search-modal', SearchModal);

  /* --------------------------------------------------------- promo popup */

  class PromoPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.key = `lumen-popup-${this.dataset.key}`;
      this.dialog.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]') || e.target === this.dialog) this.close();
        const copy = e.target.closest('[data-copy]');
        if (copy && navigator.clipboard) {
          navigator.clipboard.writeText(copy.dataset.copy).then(() => {
            copy.classList.add('is-copied');
            copy.setAttribute('aria-label', S.copied);
          });
        }
      });
      this.dialog.addEventListener('cancel', () => this.remember());

      if (this.hasAttribute('data-preview')) {
        this.dialog.showModal();
        return;
      }
      if (this.querySelector('[data-popup-success]')) {
        this.dialog.showModal();
        return;
      }
      const last = Number(storage.get('localStorage', this.key)) || 0;
      const days = Number(this.dataset.frequency) || 7;
      if (Date.now() - last < days * 86400000) return;
      setTimeout(() => {
        if (!document.querySelector('dialog[open]')) this.dialog.showModal();
      }, (Number(this.dataset.delay) || 0) * 1000);
    }

    remember() {
      if (!designMode) storage.set('localStorage', this.key, String(Date.now()));
    }

    close() {
      this.remember();
      this.dialog.close();
    }
  }
  customElements.define('promo-popup', PromoPopup);

  /* --------------------------------------------------------- scroll to top */

  class ScrollTopButton extends HTMLElement {
    connectedCallback() {
      this.hidden = false;
      const update = () => this.classList.toggle('is-visible', window.scrollY > 600);
      window.addEventListener('scroll', debounce(update, 50), { passive: true });
      update();
      this.querySelector('button').addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
      });
    }
  }
  customElements.define('scroll-top-button', ScrollTopButton);

  /* ----------------------------------------------- product recommendations */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          fetch(this.dataset.url)
            .then((r) => r.text())
            .then((html) => {
              const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
            })
            .catch(() => {});
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------- misc behaviours */

  document.querySelectorAll('form[data-auto-submit]').forEach((form) => {
    const submit = debounce(() => form.submit(), 600);
    form.addEventListener('change', (e) => {
      if (e.target.type === 'number') submit();
      else form.submit();
    });
  });

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-scroll-to-form]');
    if (!link) return;
    const target =
      document.querySelector('bundle-offer') ||
      document.querySelector('variant-picker') ||
      document.querySelector('[data-main-form]');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    target.classList.add('is-highlighted');
    setTimeout(() => target.classList.remove('is-highlighted'), 1600);
  });

  if (document.body.classList.contains('has-animations')) {
    const reveals = document.querySelectorAll('.reveal');
    if (designMode || reducedMotion || !('IntersectionObserver' in window)) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -60px 0px' }
      );
      reveals.forEach((el) => observer.observe(el));
    }
  }

  if (designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      e.target.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
    });
    document.addEventListener('shopify:section:select', (e) => {
      const popup = e.target.querySelector('promo-popup dialog');
      if (popup && !popup.open) popup.showModal();
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      const popup = e.target.querySelector('promo-popup dialog');
      if (popup && popup.open) popup.close();
    });
  }

  window.LumenTheme = { formatMoney, Cart, CartUI };
})();
